import { ReactNode } from "react"

import { cn } from "@/lib/utils"

import { ActiveFiltersList } from "../filters/ActiveFiltersList"
import { FilterButton } from "../filters/FilterButton"
import { GlobalFilter } from "../filters/GlobalFilter"
import { LE_TableInstance } from "../types"
import { ClearFiltersButton } from "./ClearFiltersButton"
import { ColumnsVisibilityButton } from "./ColumnsVisibilityButton"
import { ExportButton } from "./ExportButton"
import { ToggleFullscreenButton } from "./ToggleFullscreenButton"

export function Toolbar<TData extends Record<string, any> = object>({
  table,
  data,
  classNameHeader,
  showFilters,
  showGlobalSearch,
  enableColumnVisibility,
  enableFullscreenToggle,
  enableExportButton,
  exportFilename,
  renderCustomActions,
}: {
  table: LE_TableInstance<TData>
  data: TData[]
  classNameHeader?: string
  showFilters: boolean
  showGlobalSearch: boolean | "always"
  enableColumnVisibility: boolean
  enableFullscreenToggle: boolean
  enableExportButton: boolean
  exportFilename?: string
  renderCustomActions?: (args: {
    table: LE_TableInstance<TData>
    data: TData[]
  }) => ReactNode
}) {
  const state = table.getState()

  return (
    <div
      className={cn(
        "flex flex-col gap-2 border-b border-border bg-background p-3",
        classNameHeader,
      )}
    >
      <div className="flex justify-between gap-4">
        <div className="flex h-fit items-center gap-2">
          {showFilters ? (
            <>
              <FilterButton table={table} columnFilters={state.columnFilters} />
              <ClearFiltersButton table={table} />
            </>
          ) : null}
        </div>
        <div className="flex grow flex-wrap items-center justify-end gap-2">
          {showGlobalSearch ? (
            <>
              <GlobalFilter.Input
                table={table}
                visible={showGlobalSearch === "always" ? true : undefined}
                className="max-w-64"
              />
              <GlobalFilter.ToggleButton table={table} />
            </>
          ) : null}
          {renderCustomActions?.({ table, data }) ?? null}
          {enableColumnVisibility ? <ColumnsVisibilityButton table={table} /> : null}
          {enableFullscreenToggle ? <ToggleFullscreenButton table={table} /> : null}
          {enableExportButton ? (
            <ExportButton table={table} data={data} exportFilename={exportFilename} />
          ) : null}
        </div>
      </div>
      {showFilters && (
        <ActiveFiltersList table={table} columnFilters={state.columnFilters} />
      )}
    </div>
  )
}
